import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Input } from "./input";
import { Button } from "./button";
import { cn } from "../../lib/utils";
import { DEFAULT_PHONE_REGION } from "./phoneCountries";
import {
  countryByIso,
  filterPhoneCountries,
  flagEmoji,
  formatNationalNumber,
  matchCountryByE164,
  normalizeNationalDigits,
  parsePhoneValue,
  stripPhoneInput,
  toE164,
} from "./phoneUtils";

/**
 * Searchable calling-code selector plus national number. Value is E.164 (e.g. +15125551234).
 *
 * @param {Object} props
 * @param {string} [props.id]
 * @param {string} [props.value]
 * @param {(e164: string) => void} props.onChange
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.required]
 * @param {boolean} [props.invalid]
 * @param {string} [props.className]
 * @param {string} [props.inputClassName]
 * @param {string} [props.defaultRegion]
 * @param {string} [props.placeholder]
 * @returns {JSX.Element}
 */
export default function PhoneInput({
  id,
  value = "",
  onChange,
  disabled = false,
  required = false,
  invalid = false,
  className,
  inputClassName,
  defaultRegion = DEFAULT_PHONE_REGION,
  placeholder = "Phone number",
}) {
  const parsed = parsePhoneValue(value, defaultRegion);
  const [iso, setIso] = useState(parsed.iso);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const country = countryByIso(iso) || countryByIso(DEFAULT_PHONE_REGION);
  const nationalDisplay = formatNationalNumber(iso, parsed.national);
  const filtered = filterPhoneCountries(search);

  useEffect(() => {
    if (!value) return;
    const next = parsePhoneValue(value, iso);
    if (next.iso !== iso) {
      setIso(next.iso);
    }
    // Keep a user-selected region when the number is empty or still NANP-ambiguous.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setSearch("");
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open) {
      searchRef.current?.focus();
    }
  }, [open]);

  /**
   * @param {string} nextIso
   */
  const selectRegion = (nextIso) => {
    setIso(nextIso);
    setOpen(false);
    setSearch("");
    const national = parsePhoneValue(value, nextIso).national;
    onChange(toE164(nextIso, national));
  };

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} event
   */
  const handleNationalChange = (event) => {
    const raw = event.target.value;
    if (raw.trim().startsWith("+")) {
      const stripped = stripPhoneInput(raw);
      const matched = matchCountryByE164(stripped, iso);
      if (matched) setIso(matched.iso);
      onChange(stripped === "+" ? "" : stripped);
      return;
    }
    const national = normalizeNationalDigits(iso, raw);
    onChange(toE164(iso, national));
  };

  return (
    <div className={cn("relative", className)} ref={rootRef}>
      <div
        className={cn(
          "flex min-w-0 overflow-hidden rounded-md border bg-ui-card shadow-sm",
          invalid ? "border-error" : "border-ui-border",
          "focus-within:ring-2 focus-within:ring-ui-ring focus-within:ring-offset-2 focus-within:ring-offset-ui-background"
        )}
      >
        <Button
          type="button"
          variant="ghost"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label="Country calling code"
          className="h-9 shrink-0 rounded-none border-r border-ui-border px-2.5 font-normal text-text-primary hover:bg-ui-muted"
          onClick={() => {
            if (disabled) return;
            setOpen((prev) => !prev);
          }}
        >
          <span className="mr-1" aria-hidden>
            {flagEmoji(country.iso)}
          </span>
          <span className="text-sm">+{country.dial}</span>
          <ChevronDown className="ml-1 h-4 w-4 text-text-muted" aria-hidden />
        </Button>
        <Input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          disabled={disabled}
          required={required}
          value={nationalDisplay}
          placeholder={placeholder}
          onChange={handleNationalChange}
          className={cn(
            "h-9 min-w-0 flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0",
            inputClassName
          )}
        />
      </div>
      {open && !disabled ? (
        <div className="absolute z-20 mt-1 w-full min-w-[16rem] overflow-hidden rounded-md border border-ui-border bg-ui-card shadow-profile-md">
          <div className="border-b border-ui-border p-2">
            <Input
              ref={searchRef}
              type="text"
              value={search}
              placeholder="Search country or code…"
              autoComplete="off"
              onChange={(event) => setSearch(event.target.value)}
              className="h-8 bg-ui-card"
              aria-label="Search countries"
            />
          </div>
          <div
            className="list-scrollbar max-h-60 overflow-auto py-0.5"
            role="listbox"
            aria-label="Country calling codes"
          >
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-text-muted">No matching countries</p>
            ) : (
              filtered.map((item) => (
                <Button
                  key={item.iso}
                  type="button"
                  variant="ghost"
                  role="option"
                  aria-selected={iso === item.iso}
                  className={cn(
                    "h-auto w-full justify-start rounded-none px-3 py-2 font-normal text-text-primary hover:bg-ui-muted",
                    iso === item.iso && "bg-ui-muted"
                  )}
                  onClick={() => selectRegion(item.iso)}
                >
                  <span className="mr-2 w-6 shrink-0" aria-hidden>
                    {flagEmoji(item.iso)}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-left">{item.name}</span>
                  <span className="ml-2 shrink-0 text-text-muted">+{item.dial}</span>
                </Button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
